import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Layers } from 'lucide-react'
import { Button, Input } from '@brix/ui'
import { createTenantSchema, type CreateTenantDTO } from '@/application/dto'
import { createTenantPort } from '@/lib/container'
import { useAuthStore } from '@/store/authStore'

export function OnboardingPage() {
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { user, setTenant } = useAuthStore()
  const navigate = useNavigate()

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<CreateTenantDTO>({
    resolver: zodResolver(createTenantSchema),
  })

  const nameValue = watch('name', '')

  const autoSlug = (name: string) =>
    name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')

  const onSubmit = async (data: CreateTenantDTO) => {
    if (!user) return
    setError(null)
    setIsLoading(true)
    try {
      const t = await createTenantPort.execute({ ...data, ownerUserId: user.id })
      setTenant({
        id: t.id,
        name: t.name,
        slug: t.slug,
        plan: t.plan,
        customLimits: t.customLimits,
        createdAt: t.createdAt.toISOString(),
        updatedAt: t.updatedAt.toISOString(),
      }, 'owner')
      navigate('/')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create workspace')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-violet-600">
            <Layers className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Set up your workspace</h1>
          <p className="mt-1 text-sm text-gray-500 text-center">
            Your workspace is where you and your team manage all your websites.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <Input
            label="Workspace name"
            placeholder="Acme Inc."
            {...register('name', {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setValue('slug', autoSlug(e.target.value)),
            })}
            error={errors.name?.message}
          />
          <Input
            label="Workspace URL"
            placeholder="acme"
            hint={nameValue ? `Your sites will be at: {subdomain}.brix.app` : undefined}
            {...register('slug')}
            error={errors.slug?.message}
          />

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

          <Button type="submit" loading={isLoading} className="w-full">Create workspace</Button>
        </form>
      </div>
    </div>
  )
}
